import { Spinner } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import { useEmployeeExpenseHistory } from "@/features/finances/hooks/useFinances";
import type {
  EmployeeExpense,
  EmployeeExpenseEventType,
} from "@/features/finances/types";

interface ExpenseHistoryModalProps {
  expense: EmployeeExpense;
  onClose: () => void;
}

const DOT_COLORS: Record<EmployeeExpenseEventType, string> = {
  created: "bg-gray-300 dark:bg-gray-600",
  edited: "bg-sky-400",
  approved: "bg-green-400",
  rejected: "bg-red-400",
  transfer_prepared: "bg-amber-400",
  paid: "bg-blue-400",
  booked: "bg-violet-400",
  unbooked: "bg-orange-400",
  reverted: "bg-rose-500",
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Read-only audit log of an expense's events (status changes and their reversals). */
export function ExpenseHistoryModal({ expense, onClose }: ExpenseHistoryModalProps) {
  const { data: events = [], isLoading } = useEmployeeExpenseHistory(expense.id);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Historia zmian
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {expense.employee_name} · {formatAmount(expense.amount)} {expense.currency} ·{" "}
              {expense.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none"
            aria-label="Zamknij"
          >
            ✕
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak zdarzeń.
            </p>
          ) : (
            <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
              {events.map((ev) => (
                <li key={ev.id} className="ml-5 pb-5 last:pb-0">
                  <div
                    className={`absolute -left-1 mt-1.5 w-2 h-2 rounded-full ${DOT_COLORS[ev.event_type]}`}
                  />
                  <div className="flex items-baseline justify-between gap-4 flex-wrap mb-0.5">
                    <span
                      className={`text-xs font-semibold ${ev.is_reverted ? "text-gray-400 line-through" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {ev.event_type_display}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {ev.actor_name ?? "System"} · {formatDateTime(ev.created_at)}
                    </span>
                  </div>
                  {ev.is_reverted && (
                    <span className="text-[10px] uppercase tracking-wide text-rose-500">
                      cofnięte
                    </span>
                  )}
                  {ev.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                      {ev.description}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
